import { useEffect, useRef } from 'react'

import type { GameInfo } from '../api/client'
import { PixiTumble } from '../game/PixiTumble'
import type { ResolvedTheme } from '../theme'
import { frameShellStyle, OuterBoardFrame } from './OuterBoardFrame'

interface Props {
  game: GameInfo
  /** Styling of this game. A different theme rebuilds the board. */
  theme: ResolvedTheme
  /** Called once, when the board is ready to accept commands. */
  onReady: (board: PixiTumble) => void
}

/**
 * The React → Pixi bridge for the tumble board. The same discipline as in
 * ReelsCanvas: mount once, hand over control, never touch it again.
 */
export function TumbleCanvas({ game, theme, onReady }: Props): React.JSX.Element {
  const hostRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const host = hostRef.current
    if (!host) return

    const board = new PixiTumble()
    let disposed = false

    void board.init(host, game, theme).then(() => {
      if (disposed) {
        board.destroy()
        return
      }
      onReady(board)
    })

    return () => {
      disposed = true
      board.destroy()
    }
  }, [game, theme, onReady])

  // The stock panel (gradient, border, shadow) is the placeholder cabinet.
  // A theme with its own frame art drops it — two cabinets around one board
  // is the first thing that goes wrong when art arrives.
  const panel = theme.stage.panel ? '' : ' board--bare'
  const board = <div className={`board board--tumble${panel}`} ref={hostRef} />
  if (!theme.board.frameImage || !theme.board.frameInset) return board
  return <div className="board-frame-shell" style={frameShellStyle(theme)}>{board}<OuterBoardFrame theme={theme} /></div>
}
